import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DatePicker,
  Form,
  Input,
  InputNumber,
  notification,
  Radio,
  Select,
  Typography
} from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import useMutation from 'hooks/useMutation'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import Api from 'services/api'
import {
  listPointState,
  selectedPoint,
  selectedPointDetail
} from 'store/buildingStore'
import useSWR from 'swr'
import {
  Alert,
  AlertApiResponse,
  AlertApiUser,
  AlertDraftUser,
  AlertPayload
} from 'types/alert'
import Resizer from 'utils/imageResizer'
import {
  getPointVersion,
  getPointVersionFromResponse,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import Title from 'components/atoms/Title'

import QuillEditorV2 from './QuillEditorV2'
import UserSelector from './UserSelector'
import WeekDayPicker from './WeekDayPicker'

// --- Enum map: frontend value -> API number ---
// repeat_type: 1=none, 2=repeat
const REPEAT_TYPE_MAP: Record<string, number> = { none: 1, repeat: 2 }

// repeat_unit_type (Enum/Unit): 1=weekly, 2=monthly, 3=yearly
const REPEAT_UNIT_TYPE_MAP: Record<string, number> = {
  weekly: 1,
  monthly: 2,
  yearly: 3
}

// end_type: 1=never, 2=on_date, 3=after_occurrences
const END_TYPE_MAP: Record<string, number> = {
  never: 1,
  on_date: 2,
  after_occurrences: 3
}

// --- Reverse maps: API number -> frontend string (used for form pre-fill) ---
const REPEAT_TYPE_REVERSE: Record<number, string> = { 1: 'none', 2: 'repeat' }
const REPEAT_UNIT_TYPE_REVERSE: Record<number, string> = {
  1: 'weekly',
  2: 'monthly',
  3: 'yearly'
}
const END_TYPE_REVERSE: Record<number, string> = {
  1: 'never',
  2: 'on_date',
  3: 'after_occurrences'
}

const MAX_DIGITS = 3
const MAX_VALUE = 999

/** Like Krpano.IsMemoEmpty for Quill HTML, without regex that stacks on huge &lt;img data-url&gt;. */
function isAlertBodyQuillEmpty(html: string): boolean {
  if (!html || !html.trim()) return true
  if (html.includes('<img')) return false
  const textOnly = html.replace(/<[^>]+>/g, '').trim()
  return textOnly.length === 0
}

const handleLimitIntegerKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const target = e.target as HTMLInputElement
  const value = String(target?.value ?? '')
  const selectionStart = target?.selectionStart ?? 0
  const selectionEnd = target?.selectionEnd ?? 0
  const digitsOnly = value.replace(/[^\d]/g, '')
  const selectionLength = selectionEnd - selectionStart

  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown'
  ]
  const isCtrlShortcut =
    e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())

  if (allowedKeys.includes(e.key) || isCtrlShortcut) return

  if (/^\d$/.test(e.key)) {
    const newLength = digitsOnly.length - selectionLength + 1
    if (newLength > MAX_DIGITS) e.preventDefault()
    return
  }

  e.preventDefault()
}

const handleLimitIntegerPaste = (
  e: React.ClipboardEvent,
  form: ReturnType<typeof Form.useForm>[0],
  fieldName: string
) => {
  e.preventDefault()
  const raw = (e.clipboardData?.getData('text') ?? '').replace(/[^\d]/g, '')
  if (raw === '') return
  const digits =
    raw.length > MAX_DIGITS ? raw.slice(0, 3) : raw.slice(0, MAX_DIGITS)
  const num = Math.min(MAX_VALUE, Math.max(1, parseInt(digits, 10)))
  if (!Number.isNaN(num)) form.setFieldValue(fieldName, num)
}

const handleLimitIntegerChange = (
  value: number | string | null | undefined,
  form: ReturnType<typeof Form.useForm>[0],
  fieldName: string
) => {
  const num = Number(value)
  if (value === null || value === undefined || value === '' || num < 1) {
    form.setFieldsValue({ [fieldName]: 1 })
    setTimeout(() => form.validateFields([fieldName]).catch(() => {}), 0)
  }
}

/**
 * Format AlertApiUser from API to the format used in UserSelector.
 * Handles both "users" and "alert_users" fields inconsistencies from the backend.
 */
const formatAlertApiUsers = (users: AlertApiUser[] | undefined) => {
  if (!users) return undefined
  return users.map((u: AlertApiUser) => ({
    userId: u.id ?? u.user_id,
    email: u.email,
    permission: u.role?.toLowerCase() ?? u.permission ?? 'view',
    lastName: u.last_name || (u as unknown as { lastName?: string }).lastName,
    firstName:
      u.first_name || (u as unknown as { firstName?: string }).firstName,
    organizationName:
      u.organization?.name ||
      (u as unknown as { organization?: { name: string } }).organization
        ?.name ||
      (u as unknown as { organizationName?: string }).organizationName ||
      u.organization_name
  }))
}

type Props = {
  handleCloseModal: () => void
  alertId?: number
  initialData?: Partial<Alert>
  buildingId?: string
  floorId?: number
  annotationId?: string
  pointId?: string
  onDeleteSuccess?: () => void
  onSaveSuccess?: (data: AlertApiResponse) => void
  onSaveDraft?: (payload: AlertPayload) => void
}

function ModalAlertSetting({
  handleCloseModal,
  alertId,
  initialData,
  buildingId,
  floorId,
  annotationId,
  pointId,
  onDeleteSuccess,
  onSaveSuccess,
  onSaveDraft
}: Props) {
  const { t } = useTranslation()
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const setPoints = useSetRecoilState(listPointState)
  const [form] = Form.useForm()
  const editId = alertId ?? initialData?.id

  const [isSubmiting, setIsSubmiting] = useState(false)

  // Call API get detail Alert
  // Api.get<T> automatically unwraps: .then(r => r.data.data) -> returns T directly
  // Thus use Api.get<AlertApiResponse> (no wrapping {data: ...})
  const { data: rawAlertData, isValidating: isFetchingDetail } = useSWR(
    alertId ? `v1/alerts/${alertId}` : null,
    (url) =>
      Api.get<AlertApiResponse>(url, {
        building_id: buildingId ? Number(buildingId) : '',
        floor_id: floorId ?? ''
      })
  )
  // rawAlertData: AlertApiResponse | undefined (unwrapped by Api.get)

  // Users from API response (if any) — used for UserSelector during edit
  // The actual API returns alert_users field instead of users, and uses "id" instead of "user_id"
  const alertApiUsers = rawAlertData?.alert_users ?? rawAlertData?.users

  const [isLoadingDetail, setIsLoadingDetail] = useState(!!editId)
  // Track S3 content loading — synced with Spinner
  const [isLoadingContent, setIsLoadingContent] = useState(!!editId)

  const repeatOption = Form.useWatch('repeatOption', form)
  const repeatType = Form.useWatch('type', form)
  const repeatEndType = Form.useWatch('endType', form)
  const alertDateTime = Form.useWatch('alertDateTime', form)

  // Form locking logic:
  // 1. is_sent from API is true
  // 2. Or the send_at date has passed (past) AND it's NOT a repeating alert
  const serverSendAt = rawAlertData?.send_at || initialData?.alertDateTime
  const isPast = serverSendAt ? dayjs(serverSendAt).isBefore(dayjs()) : false
  const isLocked = !!(
    rawAlertData?.is_sent ||
    initialData?.isSent ||
    (editId && isPast && repeatOption !== 'repeat')
  )

  const { trigger: getPresignedUrl } = useMutation(
    'v1/annotations/presigned-urls'
  )
  const { trigger: createOrUpdateAlert } = useMutation(
    editId ? `v1/alerts/${editId}` : 'v1/alerts'
  )
  const { trigger: deleteAlert, isMutating: isDeleting } = useMutation(
    `v1/alerts/${editId ?? 0}`
  )

  const [quillContent, setQuillContent] = useState<string>('')

  const uploadBodyToS3 = useCallback(async (): Promise<string | null> => {
    const html = quillContent || ''
    if (isAlertBodyQuillEmpty(html)) return null

    const newHtml = await Resizer.ResizedQuillImages(html)
    const file = new Blob([newHtml], { type: 'text/plain' })
    if (file.size / (1024 * 1024) > 50) {
      return null
    }

    const data = await getPresignedUrl({
      data: { extension: 'txt', fileName: 'alert-body' }
    })
    const { key, presignedUrl } = data?.data?.s3PreSignedLinks?.[0] || {}
    if (!key || !presignedUrl) return null

    await axios.put(presignedUrl, file)
    return key
  }, [quillContent, getPresignedUrl])

  const handleSubmit = () => {
    form
      .validateFields()
      .then(async () => {
        setIsSubmiting(true)
        try {
          const contentS3Url = await uploadBodyToS3()
          const values = form.getFieldsValue(true)

          // --- Map form values -> API payload ---
          // user_ids: Map user data to object with id and role (uppercase)
          const userIdsPayload =
            (values.users_data as AlertDraftUser[])?.map((u) => ({
              id: u.userId,
              role: u.permission.toUpperCase()
            })) || []

          // repeat_type (Enum): 'none'→1, 'repeat'→2
          const repeatType = REPEAT_TYPE_MAP[values.repeatOption as string] ?? 1

          // repeat_unit (Interval): the numeric interval value from InputNumber (e.g. 1, 2, 3...)
          const repeatUnit =
            values.repeatOption === 'repeat'
              ? (values.interval as number | undefined)
              : undefined

          // repeat_unit_type (Enum/Unit): 'weekly'→1, 'monthly'→2, 'yearly'→3
          const repeatUnitType =
            values.repeatOption === 'repeat'
              ? REPEAT_UNIT_TYPE_MAP[values.type as string] ?? 1
              : undefined

          // repeat_week_days: number[] (0=Sun, 1=Mon, ..., 6=Sat)
          // Send selected days if weekly. If monthly/yearly, send [] to clear previous selection.
          let repeatWeekDays: number[] | undefined
          if (values.repeatOption === 'repeat') {
            repeatWeekDays =
              values.type === 'weekly'
                ? (values.weekDays as number[] | undefined)
                : []
          }

          // end_type (Enum): 'never'→1, 'on_date'→2, 'after_occurrences'→3
          const endType =
            values.repeatOption === 'repeat'
              ? END_TYPE_MAP[values.endType as string] ?? 1
              : undefined

          // end_date: YYYY-MM-DD string, only when end_type = on_date (2)
          const endDate: string | undefined =
            values.repeatOption === 'repeat' &&
            values.endType === 'on_date' &&
            values.endDate
              ? dayjs(values.endDate).format('YYYY-MM-DD')
              : undefined

          // end_count: occurrence count, only when end_type = after_occurrences (3)
          const endCount: number | undefined =
            values.repeatOption === 'repeat' &&
            values.endType === 'after_occurrences'
              ? (values.occurrences as number | undefined)
              : undefined

          const payload: AlertPayload = {
            building_id: buildingId ? Number(buildingId) : undefined,
            floor_id: floorId ?? undefined,
            annotation_id: annotationId ?? undefined,
            point_id: pointId ?? undefined,
            send_at:
              values.alertDateTime?.toISOString?.() ?? values.alertDateTime,
            user_ids: userIdsPayload,
            title: values.title as string,
            content: contentS3Url ?? undefined,
            body_content: quillContent, // Pass the current quill content
            repeat_type: repeatType,
            ...(values.repeatOption === 'repeat' && {
              repeat_unit: repeatUnit,
              repeat_unit_type: repeatUnitType,
              ...(repeatWeekDays !== undefined && {
                repeat_week_days: repeatWeekDays
              }),
              end_type: endType,
              ...(endDate !== undefined && { end_date: endDate }),
              ...(endCount !== undefined && { end_count: endCount })
            })
          }

          if (!annotationId && onSaveDraft) {
            // Attach detailed user data for the draft persistence
            payload.extra_users_data = values.users_data
            onSaveDraft(payload)
            setIsSubmiting(false)
            return
          }

          const versionIdToSend = getPointVersion(
            pointId || currentSelectedPoint,
            pointDetail
          )
          const res: AlertApiResponse | undefined = await createOrUpdateAlert({
            method: editId ? 'patch' : 'post',
            headers: { 'X-Point-Version-ID': String(versionIdToSend) },
            data: payload,
            onError: (error) => handlePointVersionError(error, t)
          })
          if (!res) throw new Error('更新に失敗しました')

          const newVersion = getPointVersionFromResponse(res)
          syncPointVersion(
            newVersion,
            pointId || currentSelectedPoint,
            setPointDetail,
            setPoints
          )

          notification.success({
            message: editId
              ? 'アラート設定を更新しました。'
              : 'アラート設定を作成しました。'
          })

          if (onSaveSuccess) {
            onSaveSuccess(res)
          } else {
            handleCloseModal()
          }
        } catch (err: unknown) {
          const error = err as Error
          notification.error({
            message:
              error?.message ||
              (editId
                ? 'アラート設定の更新に失敗しました。'
                : 'アラート設定の作成に失敗しました。')
          })
          handleCloseModal()
        } finally {
          setIsSubmiting(false)
        }
      })
      .catch(() => {})
  }

  const handleDelete = async () => {
    setIsSubmiting(true)
    try {
      if (editId) {
        const versionIdToSend = getPointVersion(
          pointId || currentSelectedPoint,
          pointDetail
        )
        const res = await deleteAlert({
          method: 'delete',
          headers: { 'X-Point-Version-ID': String(versionIdToSend) },
          params: {
            building_id: buildingId ? Number(buildingId) : undefined,
            floor_id: floorId ?? undefined,
            point_id: pointId ?? undefined
          },
          data: undefined,
          onError: (error) => handlePointVersionError(error, t)
        })
        if (!res) throw new Error('削除に失敗しました')

        const newVersion = getPointVersionFromResponse(res)
        syncPointVersion(
          newVersion,
          pointId || currentSelectedPoint,
          setPointDetail,
          setPoints
        )
      }
      notification.success({ message: 'アラート設定を削除しました。' })
      if (onDeleteSuccess) {
        onDeleteSuccess()
      } else {
        handleCloseModal()
      }
    } catch {
      notification.error({ message: 'アラート設定の削除に失敗しました。' })
    } finally {
      setIsSubmiting(false)
    }
  }

  useEffect(() => {
    // Prefer body_content (raw HTML) over content (S3 key) when loading for Quill
    const content =
      rawAlertData?.body_content ||
      initialData?.body_content ||
      rawAlertData?.content ||
      initialData?.content

    if (!content) {
      setQuillContent('')
      setIsLoadingContent(false)
      return
    }
    setQuillContent(content)
    setIsLoadingContent(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawAlertData?.content,
    rawAlertData?.body_content,
    initialData?.content,
    initialData?.body_content
  ])

  useEffect(() => {
    // --- CREATE case: no alertId, no initialData ---
    if (!alertId && !initialData) {
      setIsLoadingDetail(false)
      form.setFieldsValue({
        repeatOption: 'none',
        type: 'weekly',
        interval: 1,
        weekDays: [],
        endType: 'never',
        endDate: dayjs(), // Issue 4: default end date to today
        occurrences: 1, // Issue 3: default occurrences to 1
        alertDateTime: dayjs()
      })
      setQuillContent('')
      return
    }

    // --- EDIT case from initialData (Alert type - frontend format) ---
    if (initialData) {
      const rs = initialData.repeatSchedule
      const weekDaysValue: number[] = Array.isArray(rs?.weekDays)
        ? (rs?.weekDays as unknown as number[])
        : []
      form.setFieldsValue({
        title: initialData.title,
        alertDateTime: initialData.alertDateTime
          ? dayjs(initialData.alertDateTime)
          : dayjs(),
        repeatOption: rs?.option ?? 'none',
        type: rs?.type ?? 'weekly',
        interval: rs?.interval ?? 1,
        weekDays: weekDaysValue,
        endType: rs?.endType ?? 'never',
        endDate: rs?.endDate ? dayjs(rs.endDate) : undefined,
        occurrences: rs?.occurrences
      })
      setIsLoadingDetail(false)
      return
    }

    // --- EDIT case from API response (AlertApiResponse - snake_case) ---
    // rawAlertData is fetched from GET /v1/alerts/{alertId}
    if (rawAlertData) {
      // Map repeat_type (number) → frontend string: 1→'none', 2→'repeat'
      const repeatOption =
        REPEAT_TYPE_REVERSE[rawAlertData.repeat_type] ?? 'none'

      // Map repeat_unit (number) -> frontend interval input
      const interval = rawAlertData.repeat_unit ?? 1

      // map repeat_unit_type (number) -> frontend type selection: 1->'weekly', 2->'monthly', 3->'yearly'
      const repeatType =
        REPEAT_UNIT_TYPE_REVERSE[rawAlertData.repeat_unit_type ?? 0] ?? 'weekly'

      // Map end_type (number) → frontend string: 1→'never', 2→'on_date', 3→'after_occurrences'
      const endType = END_TYPE_REVERSE[rawAlertData.end_type ?? 1] ?? 'never'

      // repeat_week_days: number[] from API (e.g. [0,1,2])
      const weekDays: number[] = Array.isArray(rawAlertData.repeat_week_days)
        ? rawAlertData.repeat_week_days
        : []

      form.setFieldsValue({
        title: rawAlertData.title,
        // send_at is ISO datetime string — map to alertDateTime
        alertDateTime: rawAlertData.send_at
          ? dayjs(rawAlertData.send_at)
          : dayjs(),
        repeatOption,
        type: repeatType,
        interval,
        weekDays,
        endType,
        endDate: rawAlertData.end_date
          ? dayjs(rawAlertData.end_date)
          : undefined,
        occurrences: rawAlertData.end_count ?? undefined
      })

      setIsLoadingDetail(false)
    }

    // Still fetching (SWR hasn't returned yet)
    if (!isFetchingDetail && alertId) {
      setIsLoadingDetail(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, initialData, form, rawAlertData, isFetchingDetail])

  return (
    <Modal
      open
      width={1100}
      className="paddingless-modal"
      onCancel={handleCloseModal}
      destroyOnClose // UI/Lifecycle Fix: Ensure the entire modal content and custom local <style> blocks are wiped out and rebuilt from scratch on reopen to prevent spinner rendering issues.
    >
      <Spinner
        spinning={
          isSubmiting ||
          isLoadingDetail ||
          isLoadingContent ||
          isDeleting ||
          isFetchingDetail
        }
      >
        <div className="pt-10 pb-[15px]">
          <style>{`
            .alert-form-labels .ant-form-item-label > label.ant-form-item-required::before {
              display: none !important;
            }
            .quill-locked .ql-editor.ql-blank::before,
            .quill-locked .ql-tooltip {
              display: none !important;
            }
            .quill-locked .ql-editor {
              background-color: #f5f5f5;
              cursor: not-allowed;
            }
            .quill-locked .ql-toolbar {
              background-color: #f5f5f5;
            }
            /* Fix Task 2: Hide placeholder immediately when editor has content or focus */
            .ql-editor:not(.ql-blank) ~ .ql-placeholder,
            .ql-editor.ql-blank:focus::before {
              display: none !important;
            }
            /* Extra fix for overlap: ensure placeholder doesn't show if there's content */
            .ql-snow .ql-editor.ql-blank::before {
              content: attr(data-placeholder);
              pointer-events: none;
              position: absolute;
            }
            .ql-snow .ql-editor:not(.ql-blank)::before {
              display: none !important;
            }
            /* Fix Task 1: Disable pointer events on the calendar icon so it doesn't trigger secondary clicks */
            .alert-end-date-picker .ant-picker-suffix {
              pointer-events: none;
            }
            /* Fix Task 1 (feed-back-1.md): Fix missing bottom border in Quill Editor */
            .quill-custom-editor .ql-container.ql-snow {
              border: 1px solid #ccc !important;
              border-bottom-left-radius: 4px;
              border-bottom-right-radius: 4px;
            }
            .quill-custom-editor .ql-toolbar.ql-snow {
              border: 1px solid #ccc !important;
              border-bottom: none !important;
              border-top-left-radius: 4px;
              border-top-right-radius: 4px;
            }
            /* UI UX Fix: Styling for InputNumber spinners to match Figma (Light gray, no border, always visible spinners) */
            .custom-number-spinner {
              background-color: #F3F4F6 !important;
              border-color: transparent !important;
              box-shadow: none !important;
            }
            .custom-number-spinner:hover,
            .custom-number-spinner.ant-input-number-focused {
              border-color: #D1D5DB !important;
            }
            .custom-number-spinner .ant-input-number-input {
              background-color: transparent !important;
              height: 100%;
            }
            .custom-number-spinner .ant-input-number-handler-wrap {
              opacity: 1 !important; /* Force the up/down arrow buttons to be always visible */
              background-color: transparent !important;
            }
            /* Fix: Ensure disabled primary button has white text as per user request */
            .ant-btn-primary[disabled],
            .ant-btn-primary[disabled]:hover {
              background-color: #3f63b6 !important;
              color: #fff !important;
              opacity: 0.5;
              border: none;
            }
          `}</style>
          <Title title="アラート設定" />

          <Form
            form={form}
            labelCol={{ span: 5 }}
            labelAlign="left"
            wrapperCol={{ span: 19 }}
            colon={false}
            className="alert-form-labels"
          >
            <div className="overflow-y-auto mx-[15px] mb-2 max-h-[70vh]">
              <div className="w-[760px] mx-auto pt-[20px]">
                {/* Field 1: Date/Time */}
                <Form.Item
                  label={
                    <div className="flex items-center">
                      <Typography className="text-[15px] leading-[21px] text-[#021120]">
                        日時
                      </Typography>
                      <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                        ※必須
                      </span>
                    </div>
                  }
                  name="alertDateTime"
                  rules={[
                    { required: true, message: '日時を選択してください' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve()
                        const isRepeating =
                          form.getFieldValue('repeatOption') === 'repeat'
                        // Only block past dates for non-recurring alerts
                        if (!isRepeating && dayjs(value).isBefore(dayjs())) {
                          return Promise.reject(
                            new Error(
                              '過去の日時は選択できません。現在以降の日時を選択してください。'
                            )
                          )
                        }
                        return Promise.resolve()
                      }
                    }
                  ]}
                >
                  <DatePicker
                    placeholder="日時を選択"
                    disabled={isLocked}
                    className="h-[38px] text-sm w-60"
                    showTime={{ format: 'HH:mm' }}
                    format="YYYY-MM-DD HH:mm"
                    allowClear={false}
                    disabledTime={(current) => {
                      const isRepeating =
                        form.getFieldValue('repeatOption') === 'repeat'
                      if (isRepeating) return {} // Allow all times for repeating anchor
                      if (!current || !current.isSame(dayjs(), 'day')) return {}
                      const now = dayjs()
                      const currentHour = now.hour()
                      const currentMinute = now.minute()
                      return {
                        disabledHours: () =>
                          Array.from({ length: currentHour }, (_, i) => i),
                        disabledMinutes: (selectedHour: number) =>
                          selectedHour === currentHour
                            ? Array.from(
                                { length: currentMinute + 1 },
                                (_, i) => i
                              )
                            : []
                      }
                    }}
                  />
                </Form.Item>

                {/* Field 2: Recurrence */}
                <Form.Item
                  label={
                    <Typography className="text-[15px] leading-[21px] text-[#021120]">
                      繰り返し
                    </Typography>
                  }
                  name="repeatOption"
                  initialValue="none"
                >
                  <Select
                    className="h-[38px] text-sm"
                    style={{ width: 240 }}
                    disabled={isLocked}
                    onChange={(val) => {
                      // Reset repeat settings to defaults when switching to repeat mode
                      if (val === 'repeat') {
                        form.setFieldsValue({
                          interval: 1,
                          type: 'weekly',
                          endType: 'never',
                          endDate: dayjs(),
                          occurrences: 1 // Reset occurrences to default 1
                        })
                      }
                    }}
                    options={[
                      { value: 'none', label: '繰り返さない' },
                      { value: 'repeat', label: 'カスタム' }
                    ]}
                  />
                </Form.Item>

                {/* Recurrence setting card — per Figma */}
                {repeatOption === 'repeat' && (
                  <Form.Item
                    label={<div className="w-[14px]" />} // Fake label to indent the card same as other inputs
                    className="!mb-5"
                  >
                    <div
                      style={{
                        border: '1px solid #CED0D4',
                        borderRadius: 8,
                        backgroundColor: '#fff',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Card header */}
                      <div
                        className="px-5 pt-4 pb-2"
                        style={{ borderBottom: '1px solid #CED0D4' }}
                      >
                        <Typography className="text-[15px] font-semibold text-[#021120]">
                          繰り返し設定
                        </Typography>
                      </div>

                      <div className="px-5 py-4 flex flex-col gap-5">
                        {/* Recurrence interval */}
                        <div className="flex gap-2 items-center h-[38px]">
                          <Typography.Text className="text-[15px] text-[#021120] shrink-0 whitespace-nowrap">
                            繰り返す間隔:
                          </Typography.Text>
                          <Form.Item name="interval" noStyle initialValue={1}>
                            <InputNumber
                              min={1}
                              max={999}
                              precision={0}
                              controls // UI Fix: Explicitly enable spinners
                              disabled={isLocked}
                              className="w-20 h-[38px] text-sm custom-number-spinner"
                              onChange={(val) =>
                                handleLimitIntegerChange(val, form, 'interval')
                              }
                              onKeyDown={(e) =>
                                handleLimitIntegerKeyDown(
                                  e as React.KeyboardEvent<HTMLInputElement>
                                )
                              }
                              onPaste={(e) =>
                                handleLimitIntegerPaste(e, form, 'interval')
                              }
                            />
                          </Form.Item>
                          <Form.Item name="type" noStyle initialValue="weekly">
                            <Select
                              className="h-[38px] text-sm"
                              style={{ minWidth: 120, width: 160 }}
                              disabled={isLocked}
                              onChange={(val) => {
                                if (val !== 'weekly') {
                                  form.setFieldValue('weekDays', [])
                                }
                              }}
                              options={[
                                { value: 'weekly', label: '週間ごと' },
                                { value: 'monthly', label: '月ごと' },
                                { value: 'yearly', label: '年ごと' }
                              ]}
                            />
                          </Form.Item>
                        </div>

                        {/* Days of week — toggle buttons (Figma style) */}
                        {repeatType === 'weekly' && (
                          <div>
                            <Typography.Text className="text-[15px] text-[#021120] flex items-center mb-2">
                              曜日:
                              <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                                ※必須
                              </span>
                            </Typography.Text>
                            <Form.Item
                              name="weekDays"
                              className="mb-0 thin-error-message"
                              initialValue={[] as number[]} // number[] to match WeekDayPicker — user selects days
                              rules={[
                                {
                                  required: true,
                                  message: '曜日を選択してください'
                                }
                              ]}
                            >
                              <WeekDayPicker disabled={isLocked} />
                            </Form.Item>
                          </div>
                        )}

                        {/* End date */}
                        <div>
                          <Typography.Text className="text-[15px] text-[#021120] block mb-2">
                            終了日:
                          </Typography.Text>
                          <div className="flex">
                            <Form.Item
                              name="endType"
                              noStyle
                              initialValue="never"
                            >
                              <Radio.Group
                                className="flex flex-col gap-3 w-[150px]"
                                disabled={isLocked}
                              >
                                <Radio value="never">
                                  <div className="flex items-center h-[38px]">
                                    なし
                                  </div>
                                </Radio>
                                <Radio value="on_date">
                                  <div className="flex items-center h-[38px]">
                                    <span className="inline-flex items-center mr-2">
                                      終了日:
                                      {repeatEndType === 'on_date' && (
                                        <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                                          ※必須
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </Radio>
                                <Radio value="after_occurrences">
                                  <div className="flex items-center h-[38px]">
                                    <span className="inline-flex items-center mr-2 whitespace-nowrap">
                                      繰り返し:
                                      {repeatEndType ===
                                        'after_occurrences' && (
                                        <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                                          ※必須
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </Radio>
                              </Radio.Group>
                            </Form.Item>

                            {/* Fix Task 1 & Blurring Issue: Placed the control inputs completely outside of Form.Item name="endType" using a Flex layout to avoid any nested onChange bubbling intercepting the state */}
                            <div className="flex flex-col gap-3 flex-1">
                              {/* Spacer for 'never' row */}
                              <div className="h-[38px]" />

                              <div className="flex items-center h-[38px]">
                                <Form.Item
                                  name="endDate"
                                  noStyle
                                  initialValue={dayjs()} // Issue 4: default end date to today
                                  rules={[
                                    {
                                      required: repeatEndType === 'on_date',
                                      message: '終了日を選択してください'
                                    },
                                    {
                                      validator: (_, value) => {
                                        if (
                                          repeatEndType !== 'on_date' ||
                                          !value
                                        )
                                          return Promise.resolve()

                                        const today = dayjs().startOf('day')
                                        const endDt =
                                          dayjs(value).startOf('day')
                                        const startDt = dayjs(
                                          form.getFieldValue('alertDateTime')
                                        ).startOf('day')

                                        if (endDt.isBefore(today)) {
                                          return Promise.reject(
                                            new Error(
                                              '終了日は過去の日時を選択できません'
                                            )
                                          )
                                        }
                                        if (endDt.isBefore(startDt)) {
                                          return Promise.reject(
                                            new Error(
                                              '終了日は開始日以降の日時を選択してください'
                                            )
                                          )
                                        }
                                        return Promise.resolve()
                                      }
                                    }
                                  ]}
                                >
                                  <DatePicker
                                    className="h-[38px] w-40 alert-end-date-picker"
                                    allowClear={false}
                                    disabled={
                                      isLocked || repeatEndType !== 'on_date'
                                    }
                                    disabledDate={(current) => {
                                      const compareDate = alertDateTime
                                        ? dayjs(alertDateTime).startOf('day')
                                        : dayjs().startOf('day')
                                      return (
                                        current && current.isBefore(compareDate)
                                      )
                                    }}
                                  />
                                </Form.Item>
                              </div>

                              <div className="flex items-center gap-2 h-[39px] mb-[2px]">
                                <Form.Item
                                  name="occurrences"
                                  noStyle
                                  initialValue={1}
                                  rules={[
                                    {
                                      required:
                                        repeatEndType === 'after_occurrences',
                                      message: '回数を入力してください'
                                    }
                                  ]}
                                >
                                  <InputNumber
                                    min={1}
                                    max={999}
                                    precision={0}
                                    controls
                                    className="w-20 h-[38px] custom-number-spinner"
                                    disabled={
                                      isLocked ||
                                      repeatEndType !== 'after_occurrences'
                                    }
                                    onChange={(val) =>
                                      handleLimitIntegerChange(
                                        val,
                                        form,
                                        'occurrences'
                                      )
                                    }
                                    onKeyDown={(e) =>
                                      handleLimitIntegerKeyDown(
                                        e as React.KeyboardEvent<HTMLInputElement>
                                      )
                                    }
                                    onPaste={(e) =>
                                      handleLimitIntegerPaste(
                                        e,
                                        form,
                                        'occurrences'
                                      )
                                    }
                                  />
                                </Form.Item>
                                <span className="text-[15px] text-[#021120]">
                                  回
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form.Item>
                )}

                {/* Field 3: User — wrapped in Form.Item to align with labelCol span=5 */}
                <Form.Item
                  name="users"
                  label={
                    <div className="flex items-center mt-[-4px]">
                      <Typography className="text-[15px] leading-[21px] text-[#021120]">
                        ユーザー
                      </Typography>
                      <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                        ※必須
                      </span>
                    </div>
                  }
                  className="!items-start thin-error-message"
                  rules={[
                    {
                      required: true,
                      type: 'array',
                      min: 1,
                      message: 'ユーザーを選択してください'
                    }
                  ]}
                >
                  <UserSelector
                    form={form}
                    fieldName="users"
                    disabled={isLocked}
                    initialUsers={
                      initialData?.users?.map((u) => ({
                        userId: u.userId,
                        email: u.email,
                        permission: u.permission || 'view',
                        lastName: u.name, // Alert type uses 'name' for full name
                        organizationName: u.organization?.name
                      })) ??
                      formatAlertApiUsers(alertApiUsers as AlertApiUser[])
                    }
                  />
                </Form.Item>

                {/* Field 4: Title */}
                <div className="mt-5 border-t border-[#EEEFF2] pt-5">
                  <Form.Item
                    label={
                      <div className="flex items-center">
                        <Typography className="text-[15px] leading-[21px] text-[#021120]">
                          タイトル
                        </Typography>
                        <span className="ml-[8px] text-[#E73A3A] text-[10px] font-bold leading-none shrink-0">
                          ※必須
                        </span>
                      </div>
                    }
                    name="title"
                    className="thin-error-message"
                    rules={[
                      { required: true, message: 'タイトルを入力してください' },
                      { max: 200, message: '200文字以内で入力してください' }
                    ]}
                  >
                    <Input
                      placeholder="入力してください"
                      disabled={isLocked}
                      className="h-[38px] w-full text-sm"
                    />
                  </Form.Item>
                </div>

                {/* Field 5: Body (Quill) */}
                <Form.Item
                  label={
                    <Typography className="text-[15px] leading-[21px] text-[#021120]">
                      本文
                    </Typography>
                  }
                  labelCol={{ span: 5 }}
                  wrapperCol={{ span: 19 }}
                >
                  <div className="flex flex-col">
                    <QuillEditorV2
                      value={quillContent}
                      onChange={setQuillContent}
                      readOnly={isLocked}
                      placeholder="入力してください"
                    />
                    <Typography.Text className="text-[#021120] text-[12px] opacity-70 mt-1">
                      {t('building.uploadLimit.alert')}
                    </Typography.Text>
                  </div>
                </Form.Item>
              </div>
            </div>

            {/* Footer buttons: Delete (left, edit only) | Cancel + Save (right) */}
            <div className="border-0 border-t border-solid border-[#EEEFF2] pt-5 pb-2">
              <div className="w-[760px] mx-auto flex justify-between items-center">
                {editId ? (
                  <Button
                    danger
                    type="primary"
                    onClick={handleDelete}
                    className="px-6"
                  >
                    削除
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-4">
                  <Button
                    sizing="w-[140px] h-[50px]"
                    className="bg-[#A6AAAF]"
                    onClick={handleCloseModal}
                  >
                    {t('translation.cancel')}
                  </Button>
                  <Button
                    sizing="w-[140px] h-[50px]"
                    type="primary"
                    disabled={isLocked}
                    onClick={handleSubmit}
                    className="bg-[#3f63b6] hover:!bg-[#36539a] border-none"
                  >
                    保存
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Spinner>
    </Modal>
  )
}

ModalAlertSetting.defaultProps = {
  alertId: undefined,
  initialData: undefined,
  buildingId: undefined,
  floorId: undefined,
  annotationId: undefined,
  pointId: undefined,
  onDeleteSuccess: undefined,
  onSaveSuccess: undefined,
  onSaveDraft: undefined
}

export default ModalAlertSetting
